import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Grid,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    Divider,
    Chip,
    Alert,
    TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../../ui/appDialogTitle';
import { LensConfigurationService, ValidDataService } from '../../../../../service/validDataService';
import { isLocalDev } from '../../../../../utils/environment';
import { formatAmountForDisplay } from '../../../../../utils';
import { Colors } from '../../../../../styles/theme';

const INITIAL_EYE_CONFIG = {
    manufacturerId: '',
    lensModelId: '',
    lensMaterialId: '',
    lensIndexId: '',
    treatmentIds: [],
    availableTreatments: [], // Treatments available for this specific config
    pricing: null, // { baseCost, baseRetailPrice, treatments: [...], totalCost, totalRetail }
    labInstructions: '', // Special instructions for lab (tint, edge, etc.)
};

export default function LensConfigurationDialog({ open, onClose, onSelect, selectedRx, existingLensData }) {
    const [manufacturers, setManufacturers] = useState([]);
    const [odLensModels, setOdLensModels] = useState([]);
    const [osLensModels, setOsLensModels] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [indexes, setIndexes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Separate configurations for each eye
    const [odConfig, setOdConfig] = useState({ ...INITIAL_EYE_CONFIG });
    const [osConfig, setOsConfig] = useState({ ...INITIAL_EYE_CONFIG });

    // Load all reference data on mount or when dialog opens
    useEffect(() => {
        if (open) {
            loadReferenceData();
            
            // If editing existing data, populate the form
            if (existingLensData?.OD && existingLensData?.OS) {
                populateExistingData(existingLensData);
            } else {
                // Reset on open for new configuration
                setOdConfig({ ...INITIAL_EYE_CONFIG });
                setOsConfig({ ...INITIAL_EYE_CONFIG });
                setOdLensModels([]);
                setOsLensModels([]);
            }
        }
    }, [open, existingLensData]);

    const loadReferenceData = async () => {
        try {
            const [mfgResult, matResult, idxResult] = await Promise.all([
                ValidDataService.getManufacturersByType('lens'),
                LensConfigurationService.getMaterialsWithLimit(0, 1000),
                LensConfigurationService.getIndexesWithLimit(0, 1000),
            ]);
            if (mfgResult.code === 0) setManufacturers(mfgResult.data || []);
            if (matResult.code === 0) setMaterials(matResult.data?.rows || matResult.data || []);
            if (idxResult.code === 0) setIndexes(idxResult.data?.rows || idxResult.data || []);
        } catch (error) {
            if (isLocalDev) console.error('Failed to load reference data', error);
            toast.error('Failed to load lens configuration options');
        }
    };

    // Populate form with existing lens data for editing
    const populateExistingData = async (lensData) => {
        if (lensData.OD) {
            const odData = lensData.OD;
            // Load models for the manufacturer
            if (odData.manufacturerId) {
                try {
                    const result = await LensConfigurationService.getModelsByManufacturer(odData.manufacturerId);
                    if (result.code === 0) {
                        setOdLensModels(result.data || []);
                    }
                } catch (error) {
                    if (isLocalDev) console.error('Failed to load OD lens models', error);
                }
            }
            
            setOdConfig({
                manufacturerId: odData.manufacturerId || '',
                lensModelId: odData.lensModelId || '',
                lensMaterialId: odData.lensMaterialId || '',
                lensIndexId: odData.lensIndexId || '',
                treatmentIds: odData.treatmentIds || [],
                availableTreatments: odData.availableTreatments || [],
                pricing: odData.pricing || null,
                labInstructions: odData.labInstructions || '',
            });
        }

        if (lensData.OS) {
            const osData = lensData.OS;
            // Load models for the manufacturer
            if (osData.manufacturerId) {
                try {
                    const result = await LensConfigurationService.getModelsByManufacturer(osData.manufacturerId);
                    if (result.code === 0) {
                        setOsLensModels(result.data || []);
                    }
                } catch (error) {
                    if (isLocalDev) console.error('Failed to load OS lens models', error);
                }
            }
            
            setOsConfig({
                manufacturerId: osData.manufacturerId || '',
                lensModelId: osData.lensModelId || '',
                lensMaterialId: osData.lensMaterialId || '',
                lensIndexId: osData.lensIndexId || '',
                treatmentIds: osData.treatmentIds || [],
                availableTreatments: osData.availableTreatments || [],
                pricing: osData.pricing || null,
                labInstructions: osData.labInstructions || '',
            });
        }
    };

    // Handle manufacturer change - load models
    const handleManufacturerChange = async (manufacturerId, eye) => {
        const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
        const setModels = eye === 'OD' ? setOdLensModels : setOsLensModels;
        
        updateConfig(prev => ({
            ...prev,
            manufacturerId,
            lensModelId: '',
            pricing: null,
        }));

        if (manufacturerId) {
            try {
                const result = await LensConfigurationService.getModelsByManufacturer(manufacturerId);
                if (result.code === 0) {
                    setModels(result.data || []);
                }
            } catch (error) {
                if (isLocalDev) console.error('Failed to load lens models', error);
                toast.error('Failed to load lens models');
            }
        } else {
            setModels([]);
        }
    };

    // Handle field changes
    const handleConfigChange = (field, value, eye) => {
        const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
        updateConfig(prev => ({
            ...prev,
            [field]: value,
            // Only reset pricing if it's not labInstructions (which doesn't affect pricing)
            pricing: field === 'labInstructions' ? prev.pricing : null,
        }));
    };

    // Copy configuration from one eye to the other
    const handleCopyConfig = async (fromEye) => {
        if (fromEye === 'OD') {
            // Copy OD to OS
            const copiedConfig = { ...odConfig };
            setOsConfig(copiedConfig);
            setOsLensModels([...odLensModels]);
            
            // Recalculate pricing for OS with the copied configuration
            if (copiedConfig.lensModelId && copiedConfig.lensMaterialId && copiedConfig.lensIndexId) {
                await calculatePricing(copiedConfig, 'OS');
            }
        } else {
            // Copy OS to OD
            const copiedConfig = { ...osConfig };
            setOdConfig(copiedConfig);
            setOdLensModels([...osLensModels]);
            
            // Recalculate pricing for OD with the copied configuration
            if (copiedConfig.lensModelId && copiedConfig.lensMaterialId && copiedConfig.lensIndexId) {
                await calculatePricing(copiedConfig, 'OD');
            }
        }
    };

    // Calculate pricing when configuration is complete
    const calculatePricing = async (config, eye) => {
        const { lensModelId, lensMaterialId, lensIndexId, treatmentIds } = config;

        if (!lensModelId || !lensMaterialId || !lensIndexId) {
            return; // Not ready yet
        }

        setLoading(true);
        try {
            const result = await LensConfigurationService.getPricing(
                lensModelId,
                lensMaterialId,
                lensIndexId,
                treatmentIds || []
            );

            if (result.code === 0) {
                const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
                updateConfig(prev => ({
                    ...prev,
                    pricing: result.data,
                    availableTreatments: result.data?.availableTreatments || [],
                }));
            } else {
                toast.warning(result.message || 'No pricing found for this configuration');
                // Clear available treatments if no pricing found
                const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
                updateConfig(prev => ({
                    ...prev,
                    availableTreatments: [],
                    treatmentIds: [],
                }));
            }
        } catch (error) {
            if (isLocalDev) console.error('Failed to get pricing', error);
            toast.error('Failed to calculate lens pricing');
        } finally {
            setLoading(false);
        }
    };

    // Trigger pricing calculation when config is complete
    useEffect(() => {
        if (odConfig.lensModelId && odConfig.lensMaterialId && odConfig.lensIndexId) {
            calculatePricing(odConfig, 'OD');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [odConfig.lensModelId, odConfig.lensMaterialId, odConfig.lensIndexId, odConfig.treatmentIds]);

    useEffect(() => {
        if (osConfig.lensModelId && osConfig.lensMaterialId && osConfig.lensIndexId) {
            calculatePricing(osConfig, 'OS');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [osConfig.lensModelId, osConfig.lensMaterialId, osConfig.lensIndexId, osConfig.treatmentIds]);

    // Get selected model details
    const getSelectedModel = (modelId, eye) => {
        const models = eye === 'OD' ? odLensModels : osLensModels;
        return models.find(m => m.id === modelId);
    };

    const handleSave = () => {
        // Validate OD
        if (!odConfig.lensModelId || !odConfig.lensMaterialId || !odConfig.lensIndexId) {
            toast.error('Please complete the lens configuration for OD (Right Eye)');
            return;
        }

        if (!odConfig.pricing) {
            toast.error('Pricing not available for OD. Please check your configuration.');
            return;
        }

        // Validate OS
        if (!osConfig.lensModelId || !osConfig.lensMaterialId || !osConfig.lensIndexId) {
            toast.error('Please complete the lens configuration for OS (Left Eye)');
            return;
        }
        if (!osConfig.pricing) {
            toast.error('Pricing not available for OS. Please check your configuration.');
            return;
        }

        // Return both configurations with full snapshot objects
        const lensConfigurations = {
            OD: {
                ...odConfig,
                manufacturer: manufacturers.find(m => m.id === odConfig.manufacturerId),
                model: getSelectedModel(odConfig.lensModelId, 'OD'),
                material: materials.find(m => m.id === odConfig.lensMaterialId),
                index: indexes.find(i => i.id === odConfig.lensIndexId),
                treatments: odConfig.availableTreatments.filter(t => odConfig.treatmentIds.includes(t.id)),
                rx: selectedRx?.OD || null,
            },
            OS: {
                ...osConfig,
                manufacturer: manufacturers.find(m => m.id === osConfig.manufacturerId),
                model: getSelectedModel(osConfig.lensModelId, 'OS'),
                material: materials.find(m => m.id === osConfig.lensMaterialId),
                index: indexes.find(i => i.id === osConfig.lensIndexId),
                treatments: osConfig.availableTreatments.filter(t => osConfig.treatmentIds.includes(t.id)),
                rx: selectedRx?.OS || null,
            },
        };

        onSelect(lensConfigurations);
        onClose();
    };

    const renderEyeConfiguration = (eye, config, setConfig) => {
        const selectedModel = getSelectedModel(config.lensModelId, eye);
        const lensModels = eye === 'OD' ? odLensModels : osLensModels;

        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {eye === 'OD' ? 'OD - Right Eye' : 'OS - Left Eye'}
                        </Typography>
                        {selectedModel?.lensType && (
                            <Chip 
                                label={selectedModel.lensType.replace('_', ' ').toUpperCase()} 
                                size="small" 
                                color="primary"
                            />
                        )}
                    </Box>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleCopyConfig(eye)}
                    >
                        Copy to {eye === 'OD' ? 'OS' : 'OD'}
                    </Button>
                </Box>

                <Grid container spacing={2}>
                    {/* Manufacturer Filter */}
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Manufacturer</InputLabel>
                            <Select
                                value={config.manufacturerId}
                                onChange={(e) => handleManufacturerChange(e.target.value, eye)}
                                label="Filter by Manufacturer"
                            >
                                <MenuItem value="">All Manufacturers</MenuItem>
                                {manufacturers.map(mfg => (
                                    <MenuItem key={mfg.id} value={mfg.id}>{mfg.name}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>Select to filter lens models</FormHelperText>
                        </FormControl>
                    </Grid>

                    {/* Lens Model */}
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Lens Model</InputLabel>
                            <Select
                                value={config.lensModelId}
                                onChange={(e) => handleConfigChange('lensModelId', e.target.value, eye)}
                                label="Lens Model"
                            >
                                {lensModels.map(model => (
                                    <MenuItem key={model.id} value={model.id}>
                                        {model.modelName}
                                        {model.manufacturerBrand?.brandName && ` (${model.manufacturerBrand.brandName})`}
                                        {model.lensType && ` - ${model.lensType.replace('_', ' ')}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Material */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Material</InputLabel>
                            <Select
                                value={config.lensMaterialId}
                                onChange={(e) => handleConfigChange('lensMaterialId', e.target.value, eye)}
                                label="Material"
                            >
                                {materials.map(mat => (
                                    <MenuItem key={mat.id} value={mat.id}>{mat.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Index */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Index</InputLabel>
                            <Select
                                value={config.lensIndexId}
                                onChange={(e) => handleConfigChange('lensIndexId', e.target.value, eye)}
                                label="Index"
                            >
                                {indexes.map(idx => (
                                    <MenuItem key={idx.id} value={idx.id}>{idx.indexValue}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Treatments */}
                    <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Treatments (Optional)
                        </Typography>
                        {config.availableTreatments && config.availableTreatments.length > 0 ? (
                            config.availableTreatments.map(treatment => (
                                <FormControlLabel
                                    key={treatment.id}
                                    control={
                                        <Checkbox
                                            checked={config.treatmentIds.includes(treatment.id)}
                                            onChange={(e) => {
                                                const newTreatmentIds = e.target.checked
                                                    ? [...config.treatmentIds, treatment.id]
                                                    : config.treatmentIds.filter(id => id !== treatment.id);
                                                handleConfigChange('treatmentIds', newTreatmentIds, eye);
                                            }}
                                            size="small"
                                        />
                                    }
                                    label={`${treatment.name} (+${formatAmountForDisplay(treatment.retailPrice || 0)})`}
                                />
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {config.lensModelId && config.lensMaterialId && config.lensIndexId 
                                    ? 'No treatments available for this lens configuration'
                                    : 'Select model, material, and index to see available treatments'}
                            </Typography>
                        )}
                    </Grid>

                    {/* Lab Instructions */}
                    <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Lab Instructions (Optional)
                        </Typography>
                        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {[
                                'Tint: Grey', 'Tint: Brown', 'Tint: Green',
                                'Gradient', 'Solid', 'Match existing',
                                'Edge: Polished', 'Edge: Bevelled', 'Edge: Rolled',
                                'Rush order', 'Redo',
                            ].map(tag => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        const current = config.labInstructions || '';
                                        const separator = current && !current.endsWith('\n') ? ', ' : '';
                                        handleConfigChange('labInstructions', current + separator + tag, eye);
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ))}
                        </Box>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            placeholder="e.g., Tint: Grey solid 50%, Edge: Polished, Match existing lenses"
                            value={config.labInstructions || ''}
                            onChange={(e) => handleConfigChange('labInstructions', e.target.value, eye)}
                            helperText="Click tags above to quick-add, or type custom instructions"
                        />
                    </Grid>

                    {/* Pricing Display */}
                    {config.pricing && (
                        <Grid item xs={12}>
                            <Box sx={{ p: 2, bgcolor: Colors.secondary, borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Base Price:</strong> {formatAmountForDisplay(config.pricing.baseRetailPrice)}
                                </Typography>
                                {config.pricing.treatments && config.pricing.treatments.length > 0 && (
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Treatments:</strong> {formatAmountForDisplay(
                                            config.pricing.treatments.reduce((sum, t) => sum + (parseFloat(t.additionalRetailPrice) || 0), 0)
                                        )}
                                    </Typography>
                                )}
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Total: {formatAmountForDisplay(config.pricing.totalRetail)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Cost: {formatAmountForDisplay(config.pricing.totalCost)}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>
        );
    };

    const grandTotal = useMemo(() => {
        let total = 0;
        if (odConfig.pricing) total += parseFloat(odConfig.pricing.totalRetail) || 0;
        if (osConfig.pricing) total += parseFloat(osConfig.pricing.totalRetail) || 0;
        return total;
    }, [odConfig.pricing, osConfig.pricing]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <AppDialogTitle 
                title={existingLensData?.OD ? "Edit Lens Configuration" : "Configure Lens"} 
                onClose={onClose} 
            />
            <DialogContent>
                {selectedRx && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Rx selected: OD SPH {selectedRx.OD?.sphere || '—'}, CYL {selectedRx.OD?.cylinder || '—'} | 
                        OS SPH {selectedRx.OS?.sphere || '—'}, CYL {selectedRx.OS?.cylinder || '—'}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {/* OD Configuration */}
                    <Grid item xs={12} md={6}>
                        {renderEyeConfiguration('OD', odConfig, setOdConfig)}
                    </Grid>

                    {/* OS Configuration */}
                    <Grid item xs={12} md={6}>
                        {renderEyeConfiguration('OS', osConfig, setOsConfig)}
                    </Grid>
                </Grid>

                {/* Grand Total */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'black', color: 'white', borderRadius: 1, textAlign: 'right' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Grand Total (OD + OS): {formatAmountForDisplay(grandTotal)}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleSave} variant="contained" disabled={loading}>
                    {existingLensData?.OD ? 'Update' : 'Add to Invoice'}
                </Button>
                <Button onClick={onClose} variant="outlined" color="cancel">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
